import { AlertTriangle, CheckCircle2, CornerDownRight, FileText, GitBranch, Pause, Plus, Trash2, X } from "lucide-react";
import { Fragment } from "react";
import { Button, cx } from "../../../components/ui";
import { branchById, describeCheck, reportBranchIds, waitLabel, type Definition, type Issue, type Step, type StepBranch } from "./model";

const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";

export interface TreeHandlers {
  selected: string | null;
  onSelect: (id: string) => void;
  /** Adds a step at a position in the trunk (branch unset) or a step branch. */
  onAdd: (at: number, branch?: string) => void;
  /** Adds a step branch started by this step. */
  onAddBranch: (stepId: string) => void;
  issuesOf: (s: Step) => Issue[];
  branchIssues: (b: StepBranch) => Issue[];
}

function AddButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center justify-center size-6 rounded-full border border-line bg-surface text-muted hover:text-accent-text hover:border-accent" aria-label={label} title={label}>
      <Plus className="size-3.5" />
    </button>
  );
}

/** One step's card: its number, name, action, check, condition, and the tree's parts (reports, waits, branches). */
export function StepCardBody({ def, s, n, issues }: { def: Definition; s: Step; n: number | string; issues: Issue[] }) {
  const errs = issues.filter((i) => i.level === "error").length;
  const warns = issues.length - errs;
  const reports = reportBranchIds(s);
  return (
    <>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted tabular w-6">{n}</span>
        <span className="font-medium text-ink flex-1 truncate">{s.name}</span>
        {s.breakAfter && <Pause className={cx("size-3.5 shrink-0", s.breakScope === "run" ? "text-bad" : "text-warn")} aria-label={s.breakScope === "run" ? "Pauses the whole run for review" : "Pauses for review"} />}
        {errs > 0 ? <AlertTriangle className="size-4 text-bad shrink-0" aria-label={`${errs} to fix`} /> : warns > 0 ? <AlertTriangle className="size-4 text-warn shrink-0" aria-label={`${warns} warning${warns === 1 ? "" : "s"}`} /> : s.verifiedBy ? <CheckCircle2 className="size-4 text-good shrink-0" aria-label="Checked" /> : null}
      </div>
      <div className="text-xs text-muted font-mono truncate">{s.kind === "manual" ? "manual step" : s.kind === "compass" ? (s.compass?.sql ? "Compass SQL" : "choose a SQL statement") : s.kind === "radar" ? `RADAR ${s.radar?.op ?? ""}` : s.infor?.class ? `${s.infor.class}.${s.infor.action || "?"}` : "choose an Infor action"}</div>
      <div className="text-xs text-ink-2 truncate">{s.kind === "manual" ? "" : describeCheck(s.verify)}</div>
      {s.when && <div className="text-xs text-warn">Only when {s.when.prompt} = {s.when.equals}</div>}
      {s.repeat && <div className="text-xs text-accent-text">For each row of {s.repeat.array}{s.repeat.where ? ` where ${s.repeat.where.attr} = ${s.repeat.where.equals}` : ""}</div>}
      {s.loop && <div className="text-xs text-accent-text">Loops up to {s.loop.max}×</div>}
      {(s.waitFor?.length ?? 0) > 0 && (
        <div className="text-xs text-accent-text flex items-center gap-1">
          <CornerDownRight className="size-3 shrink-0" aria-hidden /> Waits for {s.waitFor!.map((w) => waitLabel(def, w)).join(", ")}
        </div>
      )}
      {reports.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1" aria-label="Report branches">
          {reports.map((r) => (
            <span key={r.id} className="inline-flex items-center gap-1 rounded-full border border-dashed border-line px-2 text-[11px] text-ink-2" title={`Report branch ${r.id}: runs alongside, never holds the run`}>
              <FileText className="size-3" aria-hidden /> {r.name}
            </span>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * The tree canvas (design §8.7; docs/phase4-plan.md §4): the trunk down the left, each step branch as a lane beside the
 * step that starts it (nested branches further right), report branches as chips on their step, waits named on the card.
 */
export function TreeCanvas({ def, h }: { def: Definition; h: TreeHandlers }) {
  let n = 0;
  const number = () => ++n;
  const card = (s: Step, num: number) => (
    <button
      type="button"
      onClick={() => h.onSelect(s.id)}
      aria-pressed={h.selected === s.id}
      className={cx("w-full text-left rounded-xl border bg-surface px-3 py-2 shadow-sm transition", h.selected === s.id ? "border-accent ring-2 ring-accent/30" : "border-line hover:border-accent", s.kind === "manual" && "border-dashed")}
    >
      <StepCardBody def={def} s={s} n={num} issues={h.issuesOf(s)} />
    </button>
  );
  const lanes = (s: Step): React.ReactNode =>
    (s.branches ?? [])
      .map((id) => branchById(def, id))
      .filter((b): b is StepBranch => !!b)
      .map((b) => (
        <div key={b.id} className={cx("rounded-xl border-2 p-2 space-y-2 bg-surface-2/60", b.critical ? "border-accent/50" : "border-dashed border-line", h.selected === `branch:${b.id}` && "ring-2 ring-accent/30")}>
          <button type="button" className="w-full flex items-center gap-1.5 text-xs font-semibold text-ink-2 hover:text-accent-text" onClick={() => h.onSelect(`branch:${b.id}`)} aria-pressed={h.selected === `branch:${b.id}`}>
            <GitBranch className="size-3.5" aria-hidden /> {b.name}
            <span className={cx("ml-auto font-normal", b.critical ? "text-accent-text" : "text-muted")}>{b.critical ? "critical" : "non-critical"}</span>
            {h.branchIssues(b).some((i) => i.level === "error") && <AlertTriangle className="size-3.5 text-bad" aria-label="has problems" />}
          </button>
          {b.steps.map((x, i) => (
            <Fragment key={x.id}>
              {row(x, number())}
              <div className="flex justify-center">
                <AddButton onClick={() => h.onAdd(i + 1, b.id)} label={`Add a step to ${b.name} after ${x.name}`} />
              </div>
            </Fragment>
          ))}
          {b.steps.length === 0 && (
            <div className="flex justify-center">
              <AddButton onClick={() => h.onAdd(0, b.id)} label={`Add a step to ${b.name}`} />
            </div>
          )}
        </div>
      ));
  // A step and, beside it, the branches it starts (a plain call, so numbering stays depth first like the engine's).
  function row(s: Step, num: number) {
    const side = lanes(s);
    return (
      <div className={cx("grid gap-3 items-start", (s.branches?.some((id) => branchById(def, id)) ?? false) ? "md:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]" : "md:grid-cols-[minmax(0,22rem)]")}>
        <div className="space-y-1">
          {card(s, num)}
          <button type="button" className="inline-flex items-center gap-1 text-[11px] text-muted hover:text-accent-text" onClick={() => h.onAddBranch(s.id)}>
            <GitBranch className="size-3" aria-hidden /> Add a branch
          </button>
        </div>
        {(s.branches?.some((id) => branchById(def, id)) ?? false) && <div className="space-y-2 md:border-l-2 md:border-dashed md:border-line md:pl-3">{side}</div>}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-line bg-surface-2 p-4 overflow-x-auto">
      <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">Start</div>
      <div className="mb-2">
        <AddButton onClick={() => h.onAdd(0)} label="Add a step at the start" />
      </div>
      {def.trunk.map((s, i) => (
        <Fragment key={s.id}>
          {row(s, number())}
          <div className="my-2 md:w-[22rem] flex justify-center">
            <AddButton onClick={() => h.onAdd(i + 1)} label={`Add a step after ${s.name}`} />
          </div>
        </Fragment>
      ))}
      <div className="text-xs font-semibold text-muted uppercase tracking-wide">End</div>
    </div>
  );
}

/** The same tree as an outline (keyboard and screen readers): a step branch's steps indented under its step. */
export function TreeList({ def, h }: { def: Definition; h: TreeHandlers }) {
  let n = 0;
  const rows: React.ReactNode[] = [];
  const walk = (steps: Step[], level: number, branch?: StepBranch) =>
    steps.forEach((s) => {
      const num = ++n;
      rows.push(
        <button
          key={s.id}
          type="button"
          role="treeitem"
          aria-level={level}
          aria-selected={h.selected === s.id}
          onClick={() => h.onSelect(s.id)}
          className={cx("w-full text-left px-4 py-2 hover:bg-surface-2 focus:outline-none focus:bg-surface-2", h.selected === s.id && "bg-accent-soft")}
          style={{ paddingLeft: `${1 + (level - 1) * 1.5}rem` }}
        >
          {branch && <span className="text-[11px] text-muted">{branch.name} · </span>}
          <StepCardBody def={def} s={s} n={num} issues={h.issuesOf(s)} />
          {(s.branches ?? []).map((id) => branchById(def, id)).filter(Boolean).length > 0 && (
            <div className="text-xs text-muted">Starts: {(s.branches ?? []).map((id) => branchById(def, id)?.name).filter(Boolean).join(", ")}</div>
          )}
        </button>,
      );
      for (const id of s.branches ?? []) {
        const b = branchById(def, id);
        if (b) walk(b.steps, level + 1, b);
      }
    });
  walk(def.trunk, 1);
  return (
    <div className="rounded-xl border border-line bg-surface divide-y divide-line" role="tree" aria-label="Steps">
      {rows}
      <div className="px-4 py-2">
        <Button variant="ghost" onClick={() => h.onAdd(def.trunk.length)}>
          <Plus className="size-4" aria-hidden /> Add a step
        </Button>
      </div>
    </div>
  );
}

/** A step branch's own settings: name, critical, and removing it (its steps go with it). */
export function BranchPanel({ def, branch, issues, onChange, onDelete, onClose }: { def: Definition; branch: StepBranch; issues: Issue[]; onChange: (b: StepBranch) => void; onDelete: () => void; onClose: () => void }) {
  const starter = [...def.trunk, ...(def.branches ?? []).flatMap((b) => b.steps)].find((s) => s.branches?.includes(branch.id));
  return (
    <aside className="rounded-xl border border-line bg-surface p-4 space-y-4 text-sm" aria-label={`Edit branch ${branch.name}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs text-muted font-mono">{branch.id}</div>
          <h2 className="font-display font-semibold text-ink text-lg flex items-center gap-1.5">
            <GitBranch className="size-4" aria-hidden /> {branch.name || "Branch"}
          </h2>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" title="Delete branch" aria-label="Delete branch" onClick={onDelete}>
            <Trash2 className="size-4" />
          </Button>
          <Button variant="ghost" title="Close" aria-label="Close editor" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>
      </div>
      {issues.map((i) => (
        <p key={i.message} className={cx("text-xs", i.level === "error" ? "text-bad" : "text-warn")}>
          {i.level === "error" ? "Fix: " : "⚠ "}
          {i.message}
        </p>
      ))}
      <label className="block">
        <span className="text-ink-2 font-medium">Name</span>
        <input className={input} value={branch.name} onChange={(e) => onChange({ ...branch, name: e.target.value })} />
      </label>
      <label className="flex items-start gap-2">
        <input type="checkbox" className="mt-1" checked={branch.critical} onChange={(e) => onChange({ ...branch, critical: e.target.checked })} />
        <span>
          Critical
          <span className="block text-xs text-muted">A critical branch's failure holds the whole run until someone decides. A non-critical one pauses only itself, and the run carries on.</span>
        </span>
      </label>
      <p className="text-xs text-muted">
        Starts when {starter ? <strong>{starter.name}</strong> : "its step"} completes and runs alongside. {branch.steps.length} step{branch.steps.length === 1 ? "" : "s"}. A step that must wait for it says so on its card (Waits for).
      </p>
    </aside>
  );
}
